// public/components/ProgramRegistrationForm.tsx
import React, { useState, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { programService, Program, PROGRAM_COLLECTIONS } from '../../../firebase/programFirestoreService';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../contexts/AuthContext';
import { useUI } from '../../../components/common/UIMessageProvider';

interface RegistrationFormData {
  idNumber: string;
  idType: 'ghana-card' | string;
  fullName: string;
  gender: 'male' | 'female';
  contactNumber: string;
  address: string;
  email: string;
  agreeToTerms: boolean;
  dateOfBirth?: string;
}

const ProgramRegistrationForm: React.FC = () => {
  const { currentUser, isAuthenticated, loading: authLoading } = useAuth();
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(false);
  const [registrationClosed, setRegistrationClosed] = useState(false);
  const [registrationSubmitted, setRegistrationSubmitted] = useState(false);
  const [idValidationError, setIdValidationError] = useState<string | null>(null);
  const ui = useUI();
  
  const { register, handleSubmit, setValue, setError, clearErrors, formState: { errors } } = useForm<RegistrationFormData>();

  const formatProgramDate = (value?: Timestamp | Date | string | null): string => {
    if (!value) return 'N/A';
    const formatDate = (date: Date) => {
      const day = date.getDate();
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day},${month},${year}`;
    };

    if (value instanceof Timestamp) return formatDate(value.toDate());
    if (value instanceof Date) return formatDate(value);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 'N/A' : formatDate(parsed);
  };

  // Check if given ID exists in program subcollection for this program
  const checkIdExistsInProgram = async (id: string): Promise<any | null> => {
    if (!id || !programId || !program?.requireGhanaCard) return null;
    try {
      // Query the program's registrations subcollection
      const registrationsRef = collection(db, 'programs', programId, 'registrations');
      const q = query(
        registrationsRef,
        where('idNumber', '==', id)
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        return snapshot.docs[0].data();
      }
      return null;
    } catch (err) {
      console.error('Error checking ID existence in program registrations', err);
      return null;
    }
  };

  useEffect(() => {
    if (!authLoading && !currentUser) {
      ui.showMessage('Please login to register for this program', { type: 'warning' });
      navigate('/login', { state: { from: `/programs/${programId}/register` } });
      return;
    }

    if (currentUser) {
      setValue('email', currentUser.email || '');
    }
    
    loadProgram();
  }, [programId, currentUser, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    loadProgram();
  }, [programId]);

  const loadProgram = async () => {
    if (!programId) return;
    
    try {
      const programRef = doc(db, PROGRAM_COLLECTIONS.PROGRAMS, programId);
      const programSnap = await getDoc(programRef);
      
      if (programSnap.exists()) {
        const programData = { id: programSnap.id, ...programSnap.data() } as Program;
        setProgram(programData);
        
        const now = new Date();
        const regStart = programData.registrationStart?.toDate();
        const regEnd = programData.registrationEnd?.toDate();
        
        if (regStart && regEnd && (now < regStart || now > regEnd)) {
          setRegistrationClosed(true);
        }
      }
    } catch (error) {
      console.error('Error loading program:', error);
    }
  };

  const cleanGhanaCardId = (idNumber: string): string => {
    return idNumber.replace(/[-\s]/g, '').toUpperCase();
  };

  const validateGhanaCard = (idNumber: string): { isValid: boolean; message?: string } => {
    if (!idNumber) return { isValid: false, message: 'ID number is required' };
    
    const cleaned = cleanGhanaCardId(idNumber);
    
    // GHA format: GHA followed by 10 digits (total 13 characters, e.g., GHA0027554249)
    if (cleaned.startsWith('GHA')) {
      if (!/^GHA\d{10}$/.test(cleaned)) {
        return { 
          isValid: false, 
          message: 'Invalid Ghana Card format. Should be GHA followed by 10 digits (e.g., GHA0027554249)' 
        };
      }
      return { isValid: true };
    }
    
    // 13-digit format
    if (!/^\d{13}$/.test(cleaned)) {
      return { 
        isValid: false, 
        message: 'Invalid format. Should be 13 digits or GHA followed by 10 digits' 
      };
    }
    
    return { isValid: true };
  };

  const handleIdNumberBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    if (!program?.requireGhanaCard) return;
    const idNumber = e.target.value;
    if (!idNumber) return;
    
    clearErrors('idNumber');
    setIdValidationError(null);
    
    const cleanedId = cleanGhanaCardId(idNumber);
    
    const validation = validateGhanaCard(idNumber);
    if (!validation.isValid) {
      setError('idNumber', { type: 'manual', message: validation.message });
      return;
    }
    
    setValue('idNumber', cleanedId, { shouldValidate: true });
    
    const existing = await checkIdExistsInProgram(cleanedId);
    if (existing) {
      setError('idNumber', { 
        type: 'manual', 
        message: 'This Ghana Card is already registered for this program' 
      });
    }
  };

  const handleIdNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!program?.requireGhanaCard) {
      setValue('idNumber', e.target.value);
      return;
    }

    let value = e.target.value.toUpperCase();
    value = value.replace(/[-\s]/g, '');
    
    if (value.length >= 3 && value.substring(0, 3) === 'GHA') {
      // Keep as is
    }
    
    setValue('idNumber', value, { shouldValidate: false });
  };

  const onSubmit = async (data: RegistrationFormData) => {
    console.log('onSubmit called with data:', data);
    if (!program || !programId || !currentUser) {
      console.error('Cannot submit registration - missing program/programId/currentUser', { program, programId, currentUser });
      ui.showMessage('Unable to submit registration: missing program or user information.', { type: 'error' });
      return;
    }

    setLoading(true);
    try {
      let cleanedIdNumber: string | undefined = undefined;

      if (program.requireGhanaCard) {
        cleanedIdNumber = cleanGhanaCardId(data.idNumber || '');
        const validation = validateGhanaCard(cleanedIdNumber);
        if (!validation.isValid) {
          setError('idNumber', { type: 'manual', message: validation.message });
          setLoading(false);
          return;
        }

        const existing = await checkIdExistsInProgram(cleanedIdNumber);
        if (existing) {
          ui.showMessage(`This Ghana Card (${cleanedIdNumber}) is already registered for this program.\n\nRegistered Name: ${existing.fullName || 'N/A'}\nContact: ${existing.contactNumber || 'N/A'}`, { type: 'warning' });
          setError('idNumber', { type: 'manual', message: 'This Ghana Card is already registered for this program' });
          setLoading(false);
          return;
        }
      }

      const beneficiaryPayload: any = {
        programTitle: program.title,
        fullName: data.fullName.trim(),
        gender: data.gender,
        contactNumber: data.contactNumber.trim(),
        address: data.address.trim(),
        email: data.email.trim(),
        registrationStatus: 'pending' as const,
        registeredBy: currentUser.uid,
        registeredByEmail: currentUser.email || data.email,
      };

      if (program.requireGhanaCard && cleanedIdNumber) {
        beneficiaryPayload.idNumber = cleanedIdNumber;
        beneficiaryPayload.idType = 'ghana-card';
      }

      if (program.requireDateOfBirth && data.dateOfBirth) {
        beneficiaryPayload.dateOfBirth = Timestamp.fromDate(new Date(data.dateOfBirth));
      }

      // Use the new registerForProgram method that takes programId and beneficiaryData
      const result = await programService.registerForProgram(programId, beneficiaryPayload as any);
      
      if (result.success) {
        setRegistrationSubmitted(true);
        ui.showMessage('Thanks for registering! We will contact you soon.', { type: 'success' });
      } else {
        throw new Error(
  typeof result.error === 'string'
    ? result.error
    : 'Registration failed'
);

      }
    } catch (error: any) {
      console.error('Registration error:', error);
      
      if (error.message && (error.message.includes('already exists') || error.message.includes('duplicate'))) {
        ui.showMessage('This Ghana Card ID is already registered for this program. Please use a different ID or contact support.', { type: 'warning' });
        setError('idNumber', { 
          type: 'manual', 
          message: 'This Ghana Card is already registered for this program' 
        });
      } else {
        ui.showMessage('Registration failed. Please try again.', { type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Redirecting to login...</p>
      </div>
    );
  }
  if (!program) return <div>Loading...</div>;

  if (registrationClosed) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-800">Registration Closed</h2>
        <p className="text-gray-600 mt-2">
          Registration for this program is not currently open.
        </p>
      </div>
    );
  }

  if (program.capacity && program.registeredCount >= program.capacity) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-800">Program Full</h2>
        <p className="text-gray-600 mt-2">
          This program has reached its maximum capacity.
        </p>
      </div>
    );
  }

  if (registrationSubmitted) {
    return (
      <div className="max-w-xl mx-auto bg-white p-6 rounded-lg border border-green-200 shadow-lg mt-6 mb-12">
        <h2 className="text-2xl font-bold text-green-700">Thanks for registering!</h2>
   <p className="text-gray-700 mt-2">
  Your registration for <span className="font-semibold">{program.title}</span> has been received successfully.
</p>
<p className="text-gray-600 mt-1">
  Thank you for your interest.
</p>
<div className="mt-6">

          <button
            type="button"
            onClick={() => navigate(`/programs/${programId}`)}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm"
          >
            Back to Program Details
          </button>
        </div>
      </div>
    );
  }

  const onError = (errs: any) => {
    console.error('Validation errors:', errs);
  };

  return (
    <div className="max-w-xl mx-auto bg-white p-6 rounded-lg border border-gray-200 shadow-lg mt-6 mb-12">
      <div className="mb-4">
        <h1 className="text-lg font-bold text-gray-800">Register for {program.title}</h1>
        <p className="text-gray-600 mt-1 text-sm">{program.description}</p>
        <div className="mt-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="p-2 bg-gray-50 border border-gray-200 rounded-md flex items-center justify-between gap-2">
              <p className="text-xs text-gray-500">Program Start Date</p>
              <p className="text-sm font-medium text-gray-800">{formatProgramDate(program.startDate)}</p>
            </div>
            <div className="p-2 bg-gray-50 border border-gray-200 rounded-md flex items-center justify-between gap-2">
              <p className="text-xs text-gray-500">Program End Date</p>
              <p className="text-sm font-medium text-gray-800">{formatProgramDate(program.endDate)}</p>
            </div>
          </div>
          <div className="p-2 bg-gray-50 border border-gray-200 rounded-md flex items-center justify-between gap-2">
            <p className="text-xs text-gray-500">Location</p>
            <p className="text-sm font-medium text-gray-800 text-right">{program.location || 'Not specified'}</p>
          </div>
        </div>
        {program?.requireGhanaCard && (
          <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>Important:</strong> Each Ghana Card can only be used once per program. Duplicate registrations will be rejected.
            </p>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              {...register('fullName', { 
                required: 'Full name is required',
                minLength: { value: 2, message: 'Name must be at least 2 characters' }
              })}
              className="w-full px-2 py-1 border border-gray-300 rounded-md"
            />
            {errors.fullName && (
              <p className="text-red-500 text-sm mt-1">{errors.fullName.message}</p>
            )}
          </div>

          {program?.requireGhanaCard && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ghana Card Number *
              </label>
              <input
                type="text"
                {...register('idNumber', program?.requireGhanaCard ? { 
                  required: 'Ghana Card number is required',
                  pattern: { 
                    value: /^(GHA\d{10}|\d{13})$/i, 
                    message: 'Format: GHA followed by 10 digits (GHA0027554249) or 13-digit number' 
                  }
                } : {})}
                onChange={handleIdNumberChange}
                onBlur={handleIdNumberBlur}
                placeholder="GHA0027554249 or 13-digit number"
                className="w-full px-2 py-1 border border-gray-300 rounded-md uppercase placeholder:text-xs"
              />
              {errors.idNumber && (
                <p className="text-red-500 text-sm mt-1">{errors.idNumber.message}</p>
              )}
              {idValidationError && (
                <p className="text-red-500 text-sm mt-1">{idValidationError}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Format: GHA0027554249 (no dashes) or 13-digit number
              </p>
              <input type="hidden" value="ghana-card" {...register('idType', { required: true })} />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gender *
            </label>
            <select
              {...register('gender', { required: 'Gender is required' })}
              className="w-full px-2 py-1 border border-gray-300 rounded-md"
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            {errors.gender && (
              <p className="text-red-500 text-sm mt-1">{errors.gender.message}</p>
            )}
          </div>

          {program?.requireDateOfBirth && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
              <input
                type="date"
                {...register('dateOfBirth', program?.requireDateOfBirth ? { required: 'Date of birth is required' } : {})}
                className="w-full px-2 py-1 border border-gray-300 rounded-md"
              />
              {errors.dateOfBirth && (
                <p className="text-red-500 text-sm mt-1">{errors.dateOfBirth.message}</p>
              )}
            </div>
          )}

          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address *
            </label>
            <input
              type="text"
              {...register('address', { 
                required: 'Address is required',
                minLength: { value: 5, message: 'Please enter a valid address' }
              })}
              className="w-full px-2 py-1 border border-gray-300 rounded-md"
            />
            {errors.address && (
              <p className="text-red-500 text-sm mt-1">{errors.address.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Number *
            </label>
            <input
              type="tel"
              {...register('contactNumber', { 
                required: 'Contact number is required',
                pattern: {
                  value: /^[0-9\-\+\s\(\)]{10,15}$/,
                  message: 'Please enter a valid phone number'
                }
              })}
              className="w-full px-2 py-1 border border-gray-300 rounded-md"
            />
            {errors.contactNumber && (
              <p className="text-red-500 text-sm mt-1">{errors.contactNumber.message}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              {...register('email', { 
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              className="w-full px-2 py-1 border border-gray-300 rounded-md"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
            )}
          </div>
        </div>

        <div className="flex items-start">
          <input
            type="checkbox"
            {...register('agreeToTerms', { required: 'You must agree to the terms' })}
            id="agreeToTerms"
            className="h-4 w-4 mt-1 text-primary-600 rounded"
          />
          <label htmlFor="agreeToTerms" className="ml-2 text-sm text-gray-700">
            I agree to the terms and conditions and understand that my information will be used for program purposes only.
          </label>
        </div>
        {errors.agreeToTerms && (
          <p className="text-red-500 text-sm">{errors.agreeToTerms.message}</p>
        )}

        <div className="flex justify-end mt-4">
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 text-sm"
          >
            {loading ? 'Submitting...' : 'Submit Registration'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProgramRegistrationForm;
